import {inject, Injectable} from '@angular/core';
import * as Sentry from "@sentry/angular";
import {finalize, Observable, shareReplay} from "rxjs";
import {ExternalApiService} from "../../core/api/external-api/external-api.service";
import {I18nService} from "./i18n.service";
import {AppState} from "../../core/state/app-state";
import {PostAnalysisDto} from "../../core/dto/post-analysis-dto";
import {PostAnalysisResponseDto} from "../../core/dto/post-analysis-response-dto";
import {Product} from "../../core/models/product";
import {mergeDefined, Utils} from "../utils/generics.utils";

/**
 * Single entry point for the analysis creation: the POST /v1/web-app/analyses body is built here only.
 */
@Injectable({
  providedIn: 'root'
})
export class AnalysisCreationService {

  private externalApi = inject(ExternalApiService);
  private i18nService = inject(I18nService);

  /**
   * In-flight requests, keyed by request body.
   *
   * Several callers can ask same creation at the same time, so concurrent calls sharing the same body share one request
   * instead of creating duplicates. Entries are dropped on completion: this deduplicates
   * concurrent calls, it does not cache results.
   */
  private readonly inFlight = new Map<string, {request$: Observable<PostAnalysisResponseDto>, callers: number}>();

  private static readonly SUSPICIOUS_CALLERS = 3;

  /**
   * Creates the analysis, or resumes the ongoing one (the call is idempotent per vehicle).
   * `overrides` carries the values a caller holds but the state does not have yet, such as the
   * license being validated. An undefined override keeps the state value.
   */
  createOrResume$(state: AppState, overrides: Partial<AppState> = {}): Observable<PostAnalysisResponseDto> {
    const context = mergeDefined(state, overrides);
    const dto = {
      language: context.language ?? this.i18nService.getLanguage(),
      appOrigin: Utils.getAppOrigin(context.product ?? Product.UNKNOWN),
      companyKey: context.company?.key,
      contextOrigin: context.fleet ?? 'public',
      externalId: context.externalId,
      vehicleMileage: context.vehicle?.mileage,
      license: context.vehicle?.license?.license,
      licenseCountry: context.vehicle?.license?.countryISO,
      contact: context.contact,
      inseeCodeTravelZone: context.postcode?.codeCommune,
      zipCodeTravelZone: context.postcode?.codePostal,
    } as PostAnalysisDto;

    const key = JSON.stringify(dto);
    const pending = this.inFlight.get(key);
    if (pending) {
      pending.callers++;
      if (pending.callers === AnalysisCreationService.SUSPICIOUS_CALLERS) {
        Sentry.captureMessage('Analysis creation asked repeatedly while one is in flight', {
          level: 'warning',
          extra: {callers: pending.callers, dto}
        });
      }
      return pending.request$;
    }

    // refCount false: the request completes even if the first caller unsubscribes (an aborted
    // navigation for instance), so the callers still waiting get the response.
    const request$ = this.externalApi.createAnalysis(dto).pipe(
      finalize(() => this.inFlight.delete(key)),
      shareReplay({bufferSize: 1, refCount: false})
    );
    this.inFlight.set(key, {request$, callers: 1});
    return request$;
  }
}
